import { ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { Status } from '@app/interfaces';
import ListingAdminViewModel from '@app/models-internal/listing-admin-view-model';
import ListingContact from '@app/models/listing-contact';
import ListingViewModel from '@app/models/listing-view-model';
import {
  DateService,
  ErrorHelperService,
  ListingContactService,
  ListingService,
  LogService,
  NotificationService,
  RouteHelperService,
  UserService,
} from '@app/services';
import { Subscription } from 'rxjs';
import { StatusDisplayComponent } from '../../../common-components/status-display/status-display.component';
import { FormsModule } from '@angular/forms';
import { HelpLinkComponent } from '../../../common-components/help-link/help-link.component';
import { SpinnerComponent } from '../../../common-components/spinner/spinner.component';
import { NgIf } from '@angular/common';

/**
 * Component for configuring the listing for CheckIn/CheckOut
 *
 * Used as both a route component and inside the CheckInTab component.
 *
 *
 */
@Component({
    selector: "checkin-config",
    templateUrl: "./checkin-config.component.html",
    styleUrls: ["./checkin-config.component.less"],
    imports: [NgIf, SpinnerComponent, HelpLinkComponent, FormsModule, StatusDisplayComponent]
})
export class CheckinConfigComponent implements OnInit, OnDestroy {
  private readonly _componentName = 'CheckinConfigComponent';
  private routerEventsSubscription: Subscription;
  private _listing: ListingViewModel;

  private _checkInEnabled: boolean;

  public listingId: number;

  /**
   * Hides the "Back to Listing" button that is displayed by default.
   */
  @Input()
  public hideNavButtons = false;

  /**
   * Hides the listing title (e.g. from the Landowner Listing Admin page where the listing title is already present)
   */
  @Input()
  public hideListingTitle = false;

  /**
   * Model for displays a patch status for a model's property changes
   */
  public modelPatchStatus: Record<string, Status> = {};

  public startDate: any;
  public endDate: any;
  public retrieving: boolean;

  public listingAdmin: ListingAdminViewModel;

  public checkInMemberNotifyEnabled: boolean;
  public checkInParticipantNotifyEnabled: boolean;
  public checkInOutPrimaryNotifyTextEnabled: boolean;
  public checkInOutPrimaryNotifyEmailEnabled: boolean;

  public primaryContact: ListingContact;

  constructor(
    private notify: NotificationService,
    private listingService: ListingService,
    private listingContactService: ListingContactService,
    private dateHelper: DateService,
    private errorHelper: ErrorHelperService,
    private router: Router,
    private route: ActivatedRoute,
    private routeHelper: RouteHelperService,
    private cd: ChangeDetectorRef,
    private log: LogService,
    private userService: UserService
  ) {
    console.log(`${this._componentName}.CTOR`);

    this.routerEventsSubscription = this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.listingId = +this.routeHelper.getRouteParameter(this.route, 'id');

        this.retrieveData();
      }
    });
  }

  /**
   * Is verbose logging enabled
   */
  private get verboseLogging(): boolean {
    return this.log.verboseLogging;
  }

  @Input()
  public set listing(v: ListingViewModel) {
    this._listing = v;

    this.listingId = v.id;

    this.retrieveListingAdminData();

    console.log(`${this._componentName}.listing set v=`, v);
  }

  public get listing(): ListingViewModel {
    return this._listing;
  }

  ngOnInit() {}

  public get isAdmin(): boolean {
    return this.userService.isAdmin;
  }

  public get checkInEnabled(): boolean {
    return this._checkInEnabled;
  }
  public set checkInEnabled(v: boolean) {
    this._checkInEnabled = v;
  }

  private retrieveListingAdminData() {
    this.retrieving = true;

    if (!this.listingId) {
      this.listing = null;
      this.retrieving = false;
    }

    this.listingService.getListingAdmin(this.listingId).subscribe(
      (result) => {
        console.log(`${this._componentName} subscription result=`, result);

        this.listingAdmin = result;

        this.checkInEnabled = result.checkInEnabled;
        this.checkInMemberNotifyEnabled = result.checkInMemberNotifyEnabled;
        this.checkInParticipantNotifyEnabled = result.checkInParticipantNotifyEnabled;
        this.checkInOutPrimaryNotifyEmailEnabled = result.checkInOutPrimaryNotifyEmailEnabled;
        this.checkInOutPrimaryNotifyTextEnabled = result.checkInOutPrimaryNotifyTextEnabled;

        this.retrieving = false;
      },
      (err) => {
        console.error(err);
        
        this.notify.error('A subscription error occured', null);
        this.retrieving = false;
      }
    );
  }

  private retrieveData() {
    this.retrieving = true;

    if (!this.listingId) {
      this.listing = null;
      this.retrieving = false;
    }

    this.retrieveListingAdminData();

    this.retrievePrimaryContact();

    this.listingService.getAsViewModel(this.listingId).subscribe(
      (result) => {
        if (this.verboseLogging) {
          console.log(`${this._componentName}.retrieveData()  getAsViewModel subscription result=`, result);
        }

        this.listing = result;
        this.retrieving = false;
      },
      (err) => {
        console.error(err);
        
        this.notify.error('A subscription error occured', null);
      }
    );
  }

  private retrievePrimaryContact() {
    if (!this.listingId) {
      this.primaryContact = null;

      return;
    }

    this.listingContactService.getPrimaryContact(this.listingId).subscribe(
      (result) => {
        console.log(`${this._componentName} - getPrimaryContact - subscription result=`, result);

        this.primaryContact = result;
      },
      (err) => {
        console.error(err);
        
        this.notify.error('A subscription error occured', null);
      }
    );
  }

  /**
   * Called once, before the instance is destroyed.
   */
  ngOnDestroy(): void {
    if (this.routerEventsSubscription) {
      this.routerEventsSubscription.unsubscribe();
    }
  }

  public onCheckboxStateChange(value: boolean, propertyName: string) {
    console.log(`${this._componentName}.onCheckboxStateChange - propertyName=${propertyName}`, value);

    this.patch(propertyName, value);
  }

  private patch(propertyName: string, value: any) {
    const name = propertyName;

    if (!this.modelPatchStatus[name]) {
      this.modelPatchStatus[name] = { key: name } as Status;
      this.modelPatchStatus[name].timestamp = this.dateHelper.nowUTC;
    }

    this.modelPatchStatus[name].message = 'Saving...';
    this.modelPatchStatus[name].timestamp = this.dateHelper.nowUTC;
    this.modelPatchStatus[name].isError = false;
    this.modelPatchStatus[name].isSuccess = false;

    console.log(`${this._componentName}.patch - modelPatchStatus=`, this.modelPatchStatus);

    this.listingService.patchValue(this.listing.id, propertyName, value).subscribe(
      (result) => {
        console.log(`${this._componentName} subscription result=`, result);

        this.modelPatchStatus[name].message = 'Save Successful!';
        this.modelPatchStatus[name].timestamp = this.dateHelper.nowUTC;
        this.modelPatchStatus[name].isError = false;
        this.modelPatchStatus[name].isSuccess = true;
        this.modelPatchStatus[name].expiry = this.dateHelper.addSeconds(this.dateHelper.nowUTC, 7);
      },
      (err) => {
        console.error(err);

        this.modelPatchStatus[name].message = 'Save Error!';
        this.modelPatchStatus[name].isError = true;
        this.modelPatchStatus[name].timestamp = this.dateHelper.nowUTC;

        
        this.notify.error('A subscription error occured', null);
      }
    );
  }

  public onBackToListingClick() {
    console.log(`${this._componentName}.onBackToListingClick()`);

    this.routeHelper.navigateUpOneLevel(this.route);
  }
}
